import tinycolor from 'tinycolor2';
import { CSSProp } from 'styled-components';

export function adjustHue(hexColor: string, angle: number): string {
  // Convert hex to RGB
  const r = parseInt(hexColor.substring(1, 3), 16);
  const g = parseInt(hexColor.substring(3, 5), 16);
  const b = parseInt(hexColor.substring(5, 7), 16);

  // Convert RGB to HSL
  const { h, s, l } = rgbToHsl(r, g, b);

  // Adjust Hue
  const newHue = (h + angle / 360) % 1;

  // Convert HSL back to RGB
  const { r: newR, g: newG, b: newB } = hslToRgb(newHue, s, l);

  // Convert RGB back to hex
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  (r /= 255), (g /= 255), (b /= 255);
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

function toHex(c: number): string {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

const getDarkerTinycolorInstance = (instance: tinycolor.Instance): tinycolor.Instance => {
  if (instance.isDark()) {
    return instance;
  } else {
    return getDarkerTinycolorInstance(tinycolor.random());
  }
};

export const expandSpeakerLabelStyleMap = (
  styleMapper: { [key: string]: CSSProp },
  amount = 30
): { [key: string]: CSSProp } => {
  const mapper = styleMapper;
  const styleMapperLength = Object.keys(mapper).length;

  for (let i = 0; i < amount; i++) {
    // There is a possibility to get a bright random color that makes the lighter text on it unreadable.
    // We will avoid this with the getDarkerTinycolorInstance function.
    const randomColor = getDarkerTinycolorInstance(tinycolor.random());
    const bgColor = randomColor.toString('hex');
    const lighterColor = tinycolor(randomColor).lighten(50).toString('hex');

    mapper[styleMapperLength + i] = {
      backgroundColor: bgColor,
      borderColor: lighterColor,
      color: lighterColor,
    };
  }

  return mapper;
};
